/**
 * Try increaseing speed by using native amchart export:
 * https://www.amcharts.com/docs/v4/tutorials/generating-multi-content-pdf-export/
 */
import React, { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import reactStringReplace from 'react-string-replace';
import moment from 'moment-timezone';
import * as htmlToImage from 'html-to-image';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import pushToGTM, { EXPORT_PDF_REPORT_GA4_TAG } from '../../../utils/analytics/gtm';
import { recordEvent } from '../../../utils/analytics/local';
import { URL, COMMUNITY_FORUM } from '../../../utils/constants';
import { COLORS } from '../../../styles/constants';
import { generateInputParamsAttributesPdf } from '../../InputParameters/constants';
import { DESIGN_SPEC_ATTRIBUTES } from '../../DesignSpecifications/constants';
import { SCHEMATICS } from '../../Schematic/constants/schematics';
import {
	generateBomTableBody,
	generateOpValuesTableBody,
	adaptSchematicElementDimensions,
	adaptChartElementsDimensions
} from './utils';
import { buildVinNote } from '../../OperatingValues/utils';
import PdfExportDialog from './PdfExportDialog';
import Button from '../../Button';
import Typography from '@material-ui/core/Typography';

import onsemiBase64Logo from '../../../images/logo/base64/onsemi_logo_final_v5_t_full_color';
import { FOOTER_LINKS } from '../../Footer/constants';
import { adobeDownloadReport } from '../../../utils/analytics/adobe/adob';
import { loadFontAsBase64 } from '../../../utils/loadFont';

const PdfExport = ({ selectedOpValueCategory }) => {
	const { t } = useTranslation();
	const [dialogOpen, setDialogOpen] = useState(false);
	const myDesign = useSelector(state => state.selectedDesign.myDesign);
	const inputParametersSD = useSelector(state => state.selectedDesign.detail.inputParameters);
	const initialVinNom = useSelector(state => state.selectedDesign.initialInputParameters.vinNom);
	const rankingParameters = useSelector(state => state.selectedDesign.detail.rankingParameters);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const bomItems = useSelector(state => state.selectedDesign.detail.bom);
	const opValueCategories = useSelector(state => state.selectedDesign.detail.opValueCategories);
	const topology = useSelector(state => state.selectedDesign.topology);
	const topologyCategory = useSelector(state => state.selectedDesign.detail.rankingParameters.topologyCategory);
	const charts = useSelector(state => state.selectedDesign.detail.charts);
	const selectedCharts = useSelector(state => state.chartsSelect.selectedCharts);
	const userLoggedIn = useSelector(state => state.user.loggedIn);
	const userId = useSelector(state => state.user.id);
	const userName = useSelector(state => state.user.name);
	const progressActive = useSelector(state => state.tabbar.progressActive);

	const isUserLoggedIn = userLoggedIn && userId !== null;
	const chartIds = selectedCharts.SUMMARY.includes('All')
		? charts.map(chart => chart.chartName)
		: selectedCharts.SUMMARY;
	const privacyPolicy = FOOTER_LINKS.find(link => link.id === 'privacyPolicy');
	const termsOfUse = FOOTER_LINKS.find(link => link.id === 'termsOfUse');
	const termsOfSale = FOOTER_LINKS.find(link => link.id === 'termsOfSale');
	const INPUT_PARAMS_ATTRIBUTES_PDF = generateInputParamsAttributesPdf(inputParametersSD.supplyType);
	const translationPrefix = 'components.pdfExport.content.text';

	const printToPdf = () => {
		pushToGTM(EXPORT_PDF_REPORT_GA4_TAG);

		recordEvent('PRINT_PDF_REPORT', JSON.stringify({ icPartNumber }));
		adobeDownloadReport(icPartNumber, inputParametersSD);
		setDialogOpen(true);

		// FIX BOM formatting
		const bomFixed = bomItems.map(item => Object.assign({}, item, { selected: false }));

		bomFixed.forEach(item => {
			const substring = '<sub>';
			const replacement = '_';
			const regex = new RegExp(substring, 'g');

			item.part = item.part.replace(regex, replacement);
			item.part = item.part.replace(/<[^>]*>/g, '');
		});

		function nextStep(chartImages = [], schematicImage = null, i = 0) {
			pdfMake.vfs = pdfFonts.pdfMake.vfs;
			setFont();

			async function setFont() {
				const myFont = await loadFontAsBase64('/design-tools/fonts/NotoSansSC-Regular.ttf');

				pdfMake.vfs['NotoSansSC-Regular.ttf'] = myFont;

				pdfMake.fonts = {
					NotoSC: {
						normal: 'NotoSansSC-Regular.ttf',
						bold: 'NotoSansSC-Regular.ttf',
						italics: 'NotoSansSC-Regular.ttf',
						bolditalics: 'NotoSansSC-Regular.ttf'
					}
				};
			}

			if (i >= chartIds.length) {
				const pdfExportSetting = {
					pageSize: 'A4',
					pageOrientation: 'portrait',
					pageMargins: [40, 50, 40, 70],
					header(currentPage) {
						if (currentPage > 1) {
							return [
								{
									columns: [
										{
											text: [
												{
													text: 'WebDesigner+\u2122 '
												},
												{
													text: 'Power Supply',
													fontSize: 8
												}
											],
											link: URL.WEBDESIGNER_PLUS,
											width: 'auto',
											margin: [7, 7, 0, 0]
										},
										{
											text: '',
											width: '*'
										},
										{
											image: onsemiBase64Logo,
											width: 70,
											link: URL.ONSEMI
										}
									],
									margin: 10
								}
							];
						}
					},
					footer(currentPage, _pageCount) {
						return [
							{
								stack: [
									{
										table: {
											widths: ['45%', '*', '45%'],
											body: [
												[
													{
														text: URL.WEBDESIGNER_PLUS_SHORT,
														link: URL.WEBDESIGNER_PLUS,
														marginBottom: 3
													},
													{
														text: currentPage.toString(),
														alignment: 'center'
													},
													{
														text: moment().format('MMMM Do YYYY, h:mm:ss A Z'),
														alignment: 'right'
													}
												]
											]
										},
										layout: 'noBorders'
									},
									{
										table: {
											widths: ['65%', '35%'],
											body: [
												[
													{
														text: isUserLoggedIn
															? t('components.pdfExport.footer.design') + ' ' + myDesign.id + ': ' + myDesign.name
															: t('components.pdfExport.footer.design') + ': ' + myDesign.name
													},
													{
														text: reactStringReplace(
															'\u00A9 ^year^ Semiconductor Components Industries, LLC',
															'^year^',
															() => new Date().getFullYear()
														),
														alignment: 'right'
													}
												]
											]
										},
										layout: 'noBorders'
									}
								],
								style: 'footerItem',
								margin: [17, 25, 17, 0]
							}
						];
					},
					content: [
						{
							columns: [
								{
									stack: [
										{
											text: t(`${translationPrefix}.designReport`),
											fontSize: 18
										},
										{
											text: [
												{
													text: 'WebDesigner+\u2122 '
												},
												{
													text: 'Power Supply',
													fontSize: 10
												}
											],
											color: '#999',
											margin: [0, 3, 0, 15],
											fontSize: 11,
											link: URL.WEBDESIGNER_PLUS
										},
										isUserLoggedIn
											? {
													table: {
														widths: isUserLoggedIn ? [65, '*'] : [35, '*'],
														body: [
															isUserLoggedIn ? [t('') + ':', userName] : null,
															[t(`${translationPrefix}.designID`) + ':', myDesign.id],
															[t(`${translationPrefix}.designName`) + ':', myDesign.name],
															[
																t(`${translationPrefix}.created`) + ':',
																moment(myDesign.dateCreated)
																	.tz(moment.tz.guess(true))
																	.format('MM/DD/YYYY, hh:mm:ss A Z')
															],
															[
																t(`${translationPrefix}.lastModified`) + ':',
																moment(myDesign.dateModified)
																	.tz(moment.tz.guess(true))
																	.format('MM/DD/YYYY, hh:mm:ss A Z')
															]
														]
													},
													layout: 'noBorders'
											  }
											: {
													text: myDesign.name,
													fontSize: 10
											  }
									],
									margin: [0, 20, 0, 0],
									width: '*'
								},
								{
									image: onsemiBase64Logo,
									width: 170,
									link: URL.ONSEMI
								}
							]
						},
						{
							columns: [
								{
									stack: [
										{
											text: t('components.inputParameters.title'),
											style: 'title'
										},
										{
											table: {
												widths: [110, '*'],
												body: INPUT_PARAMS_ATTRIBUTES_PDF.filter(att => inputParametersSD[att.value]).map(comp =>
													comp.value === 'output'
														? [
																comp.label + ':',
																inputParametersSD[comp.value]
																	? t('components.inputParameters.isolated')
																	: t('components.inputParameters.nonIsolated')
														  ]
														: [comp.label + ':', inputParametersSD[comp.value] + ' ' + comp.unit]
												)
											},
											layout: 'noBorders',
											style: 'table'
										}
									]
								},
								{
									stack: [
										{
											text: t('components.design.designSpecifications.title'),
											style: 'title'
										},
										{
											table: {
												widths: [90, '*'],
												body: DESIGN_SPEC_ATTRIBUTES.filter(comp => rankingParameters[comp.value]).map(comp =>
													comp.value === 'partNumber'
														? [
																comp.label,
																{
																	text: rankingParameters[comp.value],
																	link: URL.PRODUCT_PAGE_PREFIX + rankingParameters[comp.value],
																	style: {
																		color: COLORS.LINK__HEX
																	}
																}
														  ]
														: [comp.label, t(`topologies:${comp.preUnit + rankingParameters[comp.value] + comp.unit}`)]
												)
											},
											layout: 'noBorders',
											style: 'table'
										},
										initialVinNom !== ''
											? {
													text: t(`${translationPrefix}.initialVinNom`) + ' (' + inputParametersSD.vinNom + ' V).',
													fontSize: 8,
													margin: [0, 10, 0, 0]
											  }
											: null
									]
								}
							],
							margin: [0, 35, 0, 0]
						},
						{
							stack: [
								{
									text: t('pages.design.tabs.schematic'),
									style: 'title'
								},
								{
									image: schematicImage,
									width: '550',
									alignment: 'center'
								},
								{
									text: [
										buildVinNote(inputParametersSD, initialVinNom, topology, topologyCategory),
										'\n**',
										{
											text: t('components.schematic.footer.middle'),
											color: COLORS.PRIMARY__HEX
										},
										t('components.schematic.footer.end') + '\n',
										SCHEMATICS[rankingParameters.schematic] && SCHEMATICS[rankingParameters.schematic].note
											? '*' + SCHEMATICS[rankingParameters.schematic].note
											: ''
									],
									fontSize: 7,
									margin: [0, 5, 0, 0],
									alignment: 'center'
								}
							],
							margin: [0, 35, 0, 0]
						},
						{
							text: [
								{
									text: t(`${translationPrefix}.note.message`)
								},
								{
									text: t(`${translationPrefix}.note.productPage`),
									link: URL.PRODUCT_PAGE_PREFIX + icPartNumber,
									color: COLORS.LINK__HEX
								},
								{ text: t(`${translationPrefix}.note.and`) },
								{
									text: t(`${translationPrefix}.note.datasheet`),
									link: rankingParameters.datasheetUrl === '' ? URL.SALES_SUPPORT : rankingParameters.datasheetUrl,
									color: COLORS.LINK__HEX
								},
								{ text: '.' }
							],
							unbreakable: true,
							fontSize: 8,
							margin: [0, 80, 0, 0]
						},
						{
							stack: [
								{
									text: t('components.bom.title'),
									style: 'title'
								},
								{
									table: {
										headerRows: 1,
										body: generateBomTableBody(bomFixed)
									},
									layout: 'lightHorizontalLines',
									style: 'table'
								}
							],
							width: 'auto',
							margin: [0, 30, 0, 0]
						},
						{
							stack: [
								{
									text: t('components.opValues.title'),
									style: 'title'
								},
								{
									text: buildVinNote(inputParametersSD, initialVinNom, topology, topologyCategory),
									fontSize: 8,
									margin: [0, 0, 0, 10]
								},
								{
									table: {
										headerRows: 1,
										body: generateOpValuesTableBody(opValueCategories, selectedOpValueCategory)
									},
									layout: 'lightHorizontalLines',
									style: 'table'
								}
							],
							width: 'auto',
							margin: [0, 30, 0, 0]
						},
						{
							stack: [
								{
									text: t('components.summary.charts'),
									style: 'title'
								},
								{
									stack: chartImages,
									alignment: 'center'
								}
							],
							margin: [0, 30, 0, 0]
						},
						{
							stack: [
								{
									text: t(`${translationPrefix}.references`),
									style: 'title'
								},
								{
									table: {
										widths: ['50%', '50%'],

										body: [
											[
												{
													text: [
														{
															text: 'onsemi',
															fontSize: 11
														},
														{
															text: '\u2122',
															fontSize: 8
														}
													],
													marginBottom: 8
												},
												{
													text: [
														{
															text: 'WebDesigner+\u2122 ',
															fontSize: 11
														},
														{
															text: 'Power Supply',
															fontSize: 10
														}
													]
												}
											],
											[
												{
													stack: [
														{
															text: URL.ONSEMI
														},
														{
															text: privacyPolicy.title,
															link: privacyPolicy.url,
															color: COLORS.LINK__HEX,
															marginTop: 3
														},
														{
															text: termsOfUse.title,
															link: termsOfUse.url,
															color: COLORS.LINK__HEX
														},
														{
															text: termsOfSale.title,
															link: termsOfSale.url,
															color: COLORS.LINK__HEX
														}
													],
													lineHeight: 1.7
												},
												{
													stack: [
														{
															text: URL.WEBDESIGNER_PLUS
														},
														{
															text: [
																{
																	text: COMMUNITY_FORUM.label,
																	link: COMMUNITY_FORUM.url,
																	color: COLORS.LINK__HEX
																}
															]
														}
													],
													lineHeight: 1.7
												}
											]
										]
									},
									layout: 'noBorders'
								}
							],
							margin: [0, 20, 0, 0]
						}
					],
					styles: {
						title: {
							bold: true,
							fontSize: 12,
							margin: [0, 0, 0, 12]
						},
						tableHeader: {
							bold: true
						},
						table: {
							lineHeight: 1.2
						},
						footerItem: {
							fontSize: 8
						}
					},
					defaultStyle: {
						fontSize: 9,
						font: 'NotoSC'
					}
				};

				try {
					pdfMake
						.createPdf(pdfExportSetting)
						.download(
							isUserLoggedIn
								? 'WebDesigner+ ' + myDesign.id + ' - ' + myDesign.name + ' - report.pdf'
								: 'WebDesigner+ ' + myDesign.name + ' - report.pdf',
							() => setDialogOpen(false)
						);
				} catch (error) {
					console.error('Error generating PDF export:', error);
					setDialogOpen(false);
					/* TODO: show error */
				}

				adaptSchematicElementDimensions(false);
				adaptChartElementsDimensions(false, chartIds);
				return;
			}
			htmlToImage
				.toCanvas(document.getElementById(chartIds[i]))
				.then(function (canvas) {
					const data = canvas.toDataURL('image/png');
					if (data.length > 100) {
						if ((canvas.width / canvas.height) * 270 > 500) {
							chartImages.push({
								image: data,
								width: 500,
								height: (canvas.height / canvas.width) * 500,
								margin: [0, 0, 0, 15]
							});
						} else {
							chartImages.push({
								image: data,
								width: (canvas.width / canvas.height) * 270,
								height: 270,
								margin: [0, 0, 0, 15]
							});
						}
					}
					nextStep(chartImages, schematicImage, ++i);
				})
				.catch(function (error) {
					console.error('oops, something went wrong!', error);
				});
		}

		htmlToImage
			.toPng(document.getElementById('schematic_el'), { width: 2000, height: 800 })
			.then(function (dataUrl) {
				nextStep([], dataUrl);
			})
			.catch(function (error) {
				console.error('Error converting schematic SVG to image:', error);
				setDialogOpen(false);
			});
	};

	return (
		<Fragment>
			<Button color="primary" variant="contained" disabled={progressActive} onClick={() => printToPdf()}>
				<Typography style={{ fontWeight: 600 }}>{t('components.pdfExport.content.text.designReport')}</Typography>
			</Button>

			<PdfExportDialog open={dialogOpen} />
		</Fragment>
	);
};

export default PdfExport;
